import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Compass,
  MapTrifold,
  ChartPieSlice,
  Brain,
  GridNine,
  Play,
  FileText
} from "@phosphor-icons/react";

const ModulesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const modules = [
    {
      icon: Compass,
      title: "Discovery",
      description: "Map systems, teams, and AI-relevant signals",
    },
    {
      icon: MapTrifold,
      title: "Source Map",
      description: "Organize the strongest enterprise inputs for AI",
    },
    {
      icon: ChartPieSlice,
      title: "KPI Intelligence",
      description: "Identify what the business can measure and synthesize now",
    },
    {
      icon: Brain,
      title: "Enterprise Context",
      description: "Preserve context that improves AI quality over time",
    },
    {
      icon: GridNine,
      title: "Answerability Matrix",
      description: "Show what AI can answer immediately",
    },
    {
      icon: Play,
      title: "Brief Simulator",
      description: "Preview AI-powered executive briefings",
    },
    {
      icon: FileText,
      title: "Readiness Report",
      description: "Define the best path to AI activation",
    },
  ];

  return (
    <section 
      id="modules"
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative"
      data-testid="modules-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="09" name="Modules" />
          <span className="mono-label">AI Activation Platform</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            One platform. Seven modules for <span className="gradient-text">enterprise AI activation</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg">
            Each module contributes to one shared intelligence layer that helps leadership 
            identify, activate, and scale practical AI value across the enterprise.
          </p>
        </motion.div>

        {/* Modules Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden"
          data-testid="modules-grid"
        >
          {modules.map((module, index) => (
            <motion.div
              key={module.title}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className={`module-card group cursor-pointer ${
                index === modules.length - 1 ? "col-span-2 md:col-span-1" : ""
              }`}
              style={{
                backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/b99a1bd2-14ed-4a29-ab32-1d20e8c6eee5/images/7f199da1ccf67f67fd663f8e9869273b9f019a51dbfa1f8c76073ae5565c43be.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundBlendMode: 'overlay',
              }}
              data-testid={`module-${module.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-3 group-hover:bg-[#00E5FF]/20 transition-colors">
                <module.icon size={20} weight="duotone" className="text-[#00E5FF]" />
              </div>
              <h3 className="text-base font-semibold text-white mb-1 font-['Outfit']">
                {module.title}
              </h3>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">
                {module.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Shared Foundation Visual */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8"
        >
          <div className="relative py-6 px-8 rounded-xl bg-gradient-to-r from-[#0F0F10] via-[#0A0A0B] to-[#0F0F10] border border-white/10">
            {/* Flow Lines */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-transparent to-[#00E5FF]/30" />
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
              {/* Foundation Label */}
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#06B6D4] animate-pulse" />
                <span className="text-sm font-medium text-white font-['Outfit']">
                  Unified Enterprise Context
                </span>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-6 bg-white/10" />

              {/* Context Items */}
              <div className="flex flex-wrap justify-center gap-2">
                {["Systems", "Sources", "Teams", "Metrics", "Memory"].map((item) => (
                  <span 
                    key={item}
                    className="px-3 py-1 rounded-full bg-white/5 text-xs text-[#A1A1AA]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ModulesSection;
